import { useRef, useEffect } from 'react'

/**
 * Custom hook to equalize heights of elements within each row
 * This was added for a specific client request to align items form different cards accordingly
 * https://journey-digital.atlassian.net/browse/UMW-307
 * @param {string} selector - CSS selector for elements to equalize (e.g., '.heading-match-height')
 * @param {Array} dependencies - Dependencies array for useEffect (e.g., [umbraco.Blocks])
 * @returns {React.RefObject} ref to attach to the container element
 */
export const useEqualizeHeights = (selector, dependencies = []) => {
  const containerRef = useRef(null)

  // Function to equalize heights within each row
  const equalizeHeights = () => {
    if (!containerRef.current) return

    const elements = containerRef.current.querySelectorAll(selector)

    if (elements.length === 0) return

    // Group elements by their Y position (rows)
    const rows = new Map()

    elements.forEach((element, index) => {
      const rect = element.getBoundingClientRect()
      const y = Math.round(rect.top)

      if (!rows.has(y)) {
        rows.set(y, [])
      }
      rows.get(y).push({ element, index })
    })

    // Equalize heights within each row
    rows.forEach((items) => {
      if (items.length <= 1) return

      // Reset heights to auto to get natural height
      items.forEach(({ element }) => {
        element.style.height = 'auto'
      })

      // Find the maximum height in this row
      let maxHeight = 0
      items.forEach(({ element }) => {
        const height = element.offsetHeight
        if (height > maxHeight) {
          maxHeight = height
        }
      })

      // Set all elements in this row to the maximum height
      items.forEach(({ element }) => {
        element.style.height = `${maxHeight}px`
      })
    })
  }

  // Effect to handle height equalization
  useEffect(() => {
    if (!dependencies.length || !dependencies[0]) return

    // Initial equalization
    const timeoutId = setTimeout(equalizeHeights, 100)

    // Create ResizeObserver to handle dynamic content changes
    const resizeObserver = new window.ResizeObserver(() => {
      equalizeHeights()
    })

    // Observe all elements
    if (containerRef.current) {
      const elements = containerRef.current.querySelectorAll(selector)
      elements.forEach(element => {
        resizeObserver.observe(element)
      })
    }

    // Handle window resize
    const handleResize = () => {
      equalizeHeights()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
      window.removeEventListener('resize', handleResize)
    }
  }, dependencies)

  return containerRef
}
